/**
 * HouseholdExecutiveSummaryStripe — Bloomberg-style command-center stripe
 * for the Household (Group) profile page.
 *
 * Five-tile dense band aggregating household-level rollups:
 *   • Workflow Stage (slowest member)
 *   • NTN Recommendation (household verdict)
 *   • Risk Indicators (aggregate count across all members)
 *   • Member Roster
 *   • Decision-Ready Status
 */
import { useEffect, useState } from "react";
import { apiClient } from "@/lib/api";
import { householdStageFor, PIPELINE_STAGES } from "@/lib/verifyHub";
import {
  ShieldAlert, ShieldCheck, Gavel, CheckCircle2, Activity, AlertTriangle,
  Sparkles, Users,
} from "lucide-react";

const VERDICT_STATE = {
  approve:     { label: "Recommended",                 dot: "bg-emerald-500", icon: CheckCircle2 },
  conditional: { label: "Recommended with Conditions", dot: "bg-amber-500",   icon: AlertTriangle },
  deny:        { label: "Not Recommended",             dot: "bg-rose-500",    icon: Gavel },
};

const STAGE_TONE_DOT = {
  neutral: "bg-slate-400",
  amber:   "bg-amber-400",
  violet:  "bg-violet-400",
  blue:    "bg-blue-400",
  emerald: "bg-emerald-400",
};

export default function HouseholdExecutiveSummaryStripe({ group, testid = "household-executive-summary" }) {
  const g = group;
  const h = g.household || {};
  // v1.0.8 — removed members are history; roster + risk aggregates cover active only.
  const members = (g.member_screenings || []).filter((m) => !m.removed);
  const [aggregateRisk, setAggregateRisk] = useState(null);

  // Fetch risk-indicator counts across every household member in parallel.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const results = await Promise.all(
          members.map((m) =>
            apiClient.get(`/screenings/${m.screening_id}/risk-indicators`)
              .then((r) => (r.data || []).filter((ri) => ["open", "under_review", "reviewed"].includes(ri.status)).length)
              .catch(() => 0)
          )
        );
        if (cancelled) return;
        setAggregateRisk(results.reduce((acc, n) => acc + n, 0));
      } catch {
        if (!cancelled) setAggregateRisk(0);
      }
    })();
    return () => { cancelled = true; };
  }, [g.id]);

  const stage = householdStageFor(g);
  const stageIdx = PIPELINE_STAGES.findIndex((p) => p.key === stage.key);
  // Household verdict resolution: prefer explicit `household.verdict` from the
  // API payload, then fall back to the persisted final_decision on the group
  // itself so decisioned demo/legacy households show a real verdict instead
  // of "Pending".
  const rawVerdict = h.verdict || g.final_decision || g.household_verdict_override;
  const verdict = rawVerdict ? VERDICT_STATE[rawVerdict] : null;

  const decidedMembers = members.filter((m) => m.recommendation || m.overall_status === "complete").length;
  const totalMembers = members.length;
  const isDecisionReady = stage.key === "ready" || stage.key === "complete";

  return (
    <div className="bg-[#0A192F] text-white rounded-lg overflow-hidden shadow-lg" data-testid={testid}>
      <div className="px-5 py-3 border-b border-white/8 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2.5">
          <Sparkles className="h-3.5 w-3.5 text-amber-300" />
          <span className="ntn-eyebrow text-white/70">
            Household Overview
          </span>
        </div>
        <div className="ntn-mono text-white/50">
          {g.name} · {g.id.slice(0, 8).toUpperCase()}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 divide-y md:divide-y-0 md:divide-x divide-white/8">
        {/* 1 · Stage */}
        <Tile eyebrow="Workflow Stage" icon={Activity} testid="hh-exec-stage" accent={STAGE_TONE_DOT[stage.tone] || "bg-slate-400"}>
          <div className="font-serif text-lg leading-tight" data-testid="hh-exec-stage-label">{stage.label}</div>
          <div className="text-[10px] text-white/60 mt-1 font-mono">
            {stageIdx >= 0 ? `Stage ${stageIdx + 1} of 6 · slowest member` : "—"}
          </div>
        </Tile>

        {/* 2 · NTN Recommendation (Household verdict) */}
        <Tile eyebrow="NTN Recommendation" icon={verdict?.icon || Gavel} testid="hh-exec-recommendation" accent={verdict?.dot || "bg-white/30"}>
          {verdict ? (
            <>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md font-semibold text-xs uppercase tracking-wider mt-0.5"
                style={{ background: "rgba(255,255,255,0.06)" }} data-testid={`hh-exec-rec-${rawVerdict}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${verdict.dot}`} />
                {verdict.label}
              </div>
              <div className="text-[10px] text-white/50 mt-1.5 font-mono uppercase tracking-wider">Household Verdict</div>
            </>
          ) : (
            <>
              <div className="font-serif text-lg leading-tight text-white/80" data-testid="hh-exec-rec-pending">Pending</div>
              <div className="text-[10px] text-white/60 mt-1 font-mono">Awaiting member completion</div>
            </>
          )}
        </Tile>

        {/* 3 · Risk Indicators (aggregate) */}
        <Tile eyebrow="Risk Indicators" icon={aggregateRisk > 0 ? ShieldAlert : ShieldCheck} testid="hh-exec-risk"
              accent={aggregateRisk > 0 ? "bg-amber-400" : "bg-emerald-400"}>
          {aggregateRisk === null ? (
            <div className="text-white/40 text-sm">…</div>
          ) : aggregateRisk > 0 ? (
            <>
              <div className="flex items-baseline gap-1.5">
                <span className="font-serif text-3xl text-amber-300" data-testid="hh-exec-risk-count">{aggregateRisk}</span>
                <span className="text-xs text-white/70">across members</span>
              </div>
              <div className="text-[10px] text-white/60 mt-1 font-mono uppercase tracking-wider">Risk Indicators Identified</div>
            </>
          ) : (
            <>
              <div className="font-serif text-lg leading-tight text-emerald-300" data-testid="hh-exec-risk-none">None Identified</div>
              <div className="text-[10px] text-white/60 mt-1 font-mono uppercase tracking-wider">Clean Household</div>
            </>
          )}
        </Tile>

        {/* 4 · Member Roster */}
        <Tile eyebrow="Household Roster" icon={Users} testid="hh-exec-members" accent="bg-blue-400">
          <div className="flex items-baseline gap-1.5">
            <span className="font-serif text-3xl text-white" data-testid="hh-exec-member-count">
              {decidedMembers}<span className="text-white/40 text-xl">/{totalMembers}</span>
            </span>
          </div>
          <div className="text-[10px] text-white/60 mt-1 font-mono uppercase tracking-wider">Members Decision-Ready</div>
          <div className="mt-2 h-1 w-full bg-white/8 rounded-full overflow-hidden">
            <div className={`h-full transition-all ${decidedMembers === totalMembers ? "bg-emerald-400" : "bg-blue-400"}`}
                 style={{ width: `${totalMembers ? (decidedMembers / totalMembers) * 100 : 0}%` }} />
          </div>
        </Tile>

        {/* 5 · Status */}
        <Tile eyebrow="Status" icon={isDecisionReady ? Gavel : Activity} testid="hh-exec-status"
              accent={isDecisionReady ? (verdict ? "bg-emerald-400" : "bg-amber-400") : "bg-violet-400"}>
          {verdict ? (
            <>
              <div className="font-serif text-base leading-tight text-white">Decision Recorded</div>
              <div className="text-[10px] text-white/60 mt-1 font-mono uppercase tracking-wider">Complete</div>
            </>
          ) : isDecisionReady ? (
            <>
              <div className="font-serif text-base leading-tight text-amber-200">Decision-Ready Review</div>
              <div className="text-[10px] text-white/60 mt-1 font-mono uppercase tracking-wider">Awaiting Recommendation</div>
            </>
          ) : (
            <>
              <div className="font-serif text-base leading-tight text-white/80">In Progress</div>
              <div className="text-[10px] text-white/60 mt-1 font-mono uppercase tracking-wider">Tracking Members</div>
            </>
          )}
        </Tile>
      </div>
    </div>
  );
}

function Tile({ eyebrow, icon: Icon, accent, testid, children }) {
  return (
    <div className="relative px-5 py-4" data-testid={testid}>
      <div className={`absolute left-0 top-4 bottom-4 w-0.5 ${accent} rounded-r-full opacity-80`} />
      <div className="flex items-center gap-1.5 mb-2">
        <Icon className="h-3 w-3 text-white/50" />
        <span className="ntn-eyebrow text-white/60" style={{ fontSize: "0.625rem" }}>{eyebrow}</span>
      </div>
      {children}
    </div>
  );
}
